import { useBatchStore } from '@/stores/batchStore';
import { fetchWithAuth } from '@/lib/api';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { StopCircle, X, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CANCEL_URLS: Record<string, (id: string) => string> = {
  'scp-image': (id) => `/api/admin/scp/batch-cancel/${id}`,
  'scp-chat-config': (id) => `/api/admin/scp/chat-config-cancel/${id}`,
  'card-stats': (id) => `/api/admin/cards/batch-cancel/${id}`,
  'card-images': (id) => `/api/admin/cards/image-batch-cancel/${id}`,
};

export function BatchProgressFloat() {
  const jobs = useBatchStore(s => Array.from(s.jobs.values()));
  const clear = useBatchStore(s => s.clear);

  if (jobs.length === 0) return null;

  const handleCancel = async (batchId: string, type: string) => {
    const urlFn = CANCEL_URLS[type];
    if (!urlFn) return;
    try {
      await fetchWithAuth(urlFn(batchId), { method: 'POST' });
      toast.success('중단 요청을 보냈습니다.');
    } catch {
      toast.error('중단 요청 실패');
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 space-y-3 w-96">
      {jobs.map((job) => {
        const percent = job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;
        const isRunning = job.status === 'running';
        const isDone = job.status === 'completed' || job.status === 'cancelled';
        const isError = job.status === 'error';

        return (
          <div key={job.batchId} className="bg-card border shadow-xl rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                {isRunning && <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />}
                {isDone && <CheckCircle className="h-5 w-5 text-green-500" />}
                {isError && <AlertCircle className="h-5 w-5 text-destructive" />}
                {job.label}
              </div>
              {!isRunning && (
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => clear(job.batchId)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <Progress value={percent} className="h-2.5" />

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {isRunning && `${job.done} / ${job.total}  ·  ${job.current}`}
                {job.status === 'completed' && `완료! 성공 ${job.success}건`}
                {job.status === 'cancelled' && `중단됨. 성공 ${job.success}건`}
                {isError && (job.current || '서버 연결 끊김')}
              </span>
              <span className="font-mono">{percent}%</span>
            </div>

            {job.failed > 0 && (
              <div className="text-xs text-destructive">실패 {job.failed}건</div>
            )}

            {isRunning && (
              <Button variant="destructive" size="sm" className="w-full h-8 text-xs" onClick={() => handleCancel(job.batchId, job.type)}>
                <StopCircle className="h-4 w-4 mr-1" /> 중단
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
